import { useState, useEffect } from 'react';
import { X, Cookie, Settings, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export function CookieConsent() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('cookie-consent-v2');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShow(true), 1500);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
        updateGoogleConsent(saved);
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const updateGoogleConsent = (prefs: CookiePreferences) => {
    if (typeof window === 'undefined' || typeof (window as any).gtag !== 'function') return;
    
    const gtag = (window as any).gtag;
    gtag('consent', 'update', {
      'analytics_storage': prefs.analytics ? 'granted' : 'denied',
      'ad_storage': prefs.marketing ? 'granted' : 'denied',
      'ad_user_data': prefs.marketing ? 'granted' : 'denied',
      'ad_personalization': prefs.marketing ? 'granted' : 'denied',
      'functionality_storage': prefs.functional ? 'granted' : 'denied',
      'personalization_storage': prefs.functional ? 'granted' : 'denied',
      'security_storage': 'granted',
    });

    // Cookie consent is managed by our custom component
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent-v2', JSON.stringify(allAccepted));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    updateGoogleConsent(allAccepted);
    setShow(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookie-consent-v2', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    updateGoogleConsent(onlyNecessary);
    setShow(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent-v2', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    updateGoogleConsent(preferences);
    setShowSettings(false);
    setShow(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 shadow-2xl">
      <div className="mx-auto max-w-4xl">
        {!showSettings ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-shrink-0">
              <Cookie className="w-6 h-6 text-[#e69138]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">Cookie Consent</h3>
              <p className="text-sm text-gray-300 mb-4">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                We also use cookies for Google Ads to show you relevant advertisements. Our services are provided 
                exclusively within the United States. By clicking "Accept All", you consent to our use of cookies.{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShow(false);
                    navigate('/privacy');
                  }}
                  className="text-[#e69138] hover:text-[#d17f2a] underline focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2 rounded bg-transparent border-0 p-0 cursor-pointer"
                  aria-label="Read our Privacy Policy"
                >
                  Learn more in our Privacy Policy
                </button>
                {' '}or{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShow(false);
                    navigate('/cookies');
                  }}
                  className="text-[#e69138] hover:text-[#d17f2a] underline focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2 rounded bg-transparent border-0 p-0 cursor-pointer"
                  aria-label="Read our Cookie Policy"
                >
                  Cookie Policy
                </button>
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-[#e69138] hover:bg-[#d17f2a] text-white"
                  size="sm"
                >
                  Accept All
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  size="sm"
                >
                  Reject All
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="ghost"
                  className="text-gray-300 hover:text-white hover:bg-gray-800"
                  size="sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Customize
                </Button>
              </div>
            </div>
            <button
              onClick={() => setShow(false)}
              className="text-gray-400 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Close settings"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white">Necessary Cookies</h4>
                    <span className="text-xs text-gray-400">Always Active</span>
                  </div>
                  <p className="text-sm text-gray-300">
                    Essential for the website to function properly. These cannot be disabled.
                  </p>
                </div>
                <div className="ml-4">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">Analytics Cookies</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    Help us understand how visitors interact with our website (Google Analytics).
                  </p>
                </div>
                <button
                  onClick={() => togglePreference('analytics')}
                  className={cn(
                    "ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    preferences.analytics ? "bg-[#e69138]" : "bg-gray-600"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      preferences.analytics ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">Marketing Cookies</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    Used to deliver personalized ads (Google Ads). These help us show you relevant advertisements.
                  </p>
                </div>
                <button
                  onClick={() => togglePreference('marketing')}
                  className={cn(
                    "ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    preferences.marketing ? "bg-[#e69138]" : "bg-gray-600"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      preferences.marketing ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-semibold text-white mb-1">Functional Cookies</h4>
                  <p className="text-sm text-gray-300 mb-2">
                    Enable enhanced functionality and personalization.
                  </p>
                </div>
                <button
                  onClick={() => togglePreference('functional')}
                  className={cn(
                    "ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    preferences.functional ? "bg-[#e69138]" : "bg-gray-600"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      preferences.functional ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleSavePreferences}
                className="bg-[#e69138] hover:bg-[#d17f2a] text-white"
                size="sm"
              >
                Save Preferences
              </Button>
              <Button
                onClick={handleAcceptAll}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                size="sm"
              >
                Accept All
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="ghost"
                className="text-gray-300 hover:text-white hover:bg-gray-700"
                size="sm"
              >
                Reject All
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
